import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Core } from "@faire/design-tokens";
import { isSizeVariation } from "@faire/retailer-visitor-shared/features/PrePacks/addToCartUtils";
import { titleCaseWithSpecialCharacters } from "@faire/retailer-visitor-shared/lib/string";
import { getVariantDescriptorSuffixDropdown } from "@faire/retailer-visitor-shared/views/product/ProductDetails/AddToCart/OptionSelection/getVariantDescriptorSuffix";
import { SizeChartLink } from "@faire/retailer-visitor-shared/views/product/ProductDetails/AddToCart/OptionSelection/SizeChartLink";
import { SpecialOptionPicker } from "@faire/retailer-visitor-shared/views/product/ProductDetails/AddToCart/OptionSelection/SpecialOptionPicker";
import { useStrictLocalization } from "@faire/web--source/common/localization/localize";
import { uniqueId } from "@faire/web--source/common/server-side-rendering/uniqueId";
import { Dropdown } from "@faire/web--source/slate/Dropdown";
import { Column, Row } from "@faire/web--source/slate/Layout";
import { Spacer } from "@faire/web--source/slate/spacing";
import { Typography } from "@faire/web--source/slate/Typography";
import { trackProductInfoProductDetailsVariantSelectionClick } from "@faire/web-api--source/events/productInfo/click/productDetailsVariantSelection";
import { IProductVariationMap } from "@faire/web-api--source/indigofair/data/IProductVariationMap";
import { observer } from "mobx-react";
import * as React from "react";
import styled from "styled-components";
var SHORT_OPTION_LENGTH = 5;
var SingleVariationSelectorBase = function(param) {
    var variationType = param.variationType, variationName = param.variationName, variantOptionSelectionViewState = param.variantOptionSelectionViewState, variationDisplayType = param.variationDisplayType, alt = param.alt, brand = param.brand;
    var _variationOptions__name, _variationOptions_;
    var strictLocalize = useStrictLocalization().strictLocalize;
    var PRE_PACK = strictLocalize({
        id: "OQWfu8",
        defaultMessage: [
            {
                type: 0,
                value: "Prepack"
            }
        ]
    });
    var SINGLE_OPTION = strictLocalize({
        id: "R0M65c",
        defaultMessage: [
            {
                type: 0,
                value: "Option"
            }
        ]
    });
    var SIZE_OPTION = titleCaseWithSpecialCharacters("Size");
    var viewState = variantOptionSelectionViewState;
    var getDisplayableName = function() {
        if (isSizeVariation(variationType) && viewState.currentStyleHasOnlyPrePacks) {
            return PRE_PACK;
        }
        if (!variationName || variationName.toLowerCase() === "default") {
            return SINGLE_OPTION;
        }
        return titleCaseWithSpecialCharacters(variationName);
    };
    var variationOptions = viewState.variationsGroupedByName[variationName];
    var selectedVariationValue = viewState.getSelectedVariationValue(variationName);
    var displayType = getDisplayType(variationName, viewState, variationOptions, variationDisplayType);
    var getVariantOption = function(variation) {
        var variationState = viewState.stateForAlternateVariation(variation);
        var variationName = variation.value || SINGLE_OPTION;
        var suffix = getVariantDescriptorSuffixDropdown(variationState, strictLocalize);
        var label = suffix ? "".concat(variationName, " ").concat(suffix) : variationName;
        var _variation_value;
        return {
            label: label,
            value: (_variation_value = variation.value) !== null && _variation_value !== void 0 ? _variation_value : ""
        };
    };
    var displayableName = getDisplayableName();
    var _variationOptions_map;
    var dropDownOptions = (_variationOptions_map = variationOptions === null || variationOptions === void 0 ? void 0 : variationOptions.map(function(variation) {
        return getVariantOption(variation);
    })) !== null && _variationOptions_map !== void 0 ? _variationOptions_map : [];
    var showSizeChartLink = brand.brand_size_chart && displayableName === PRE_PACK || displayableName === SIZE_OPTION;
    var showSizeChartLinkForPresetPrepack = !!brand.brand_size_chart && displayableName === PRE_PACK;
    var dropDownRefId = uniqueId("option-dropdown-label-id-");
    // The name field is not being used on the BE but needs to stick around
    // since there are >200k variations with default-default as their value.
    // These need to be removed, at which time we will be able to skip this check
    // since all default product options won't have any variations to select
    if ((variationOptions === null || variationOptions === void 0 ? void 0 : variationOptions.length) === 1 && ((_variationOptions_ = variationOptions[0]) === null || _variationOptions_ === void 0 ? void 0 : (_variationOptions__name = _variationOptions_.name) === null || _variationOptions__name === void 0 ? void 0 : _variationOptions__name.toLowerCase()) === "default") {
        return null;
    }
    // If there is only one option, don't show the dropdown
    if (displayType === IProductVariationMap.IEntry.VariationDisplayType.SINGLE) {
        return /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsx(Row, {
                    justify: "space-between",
                    align: "flex-end",
                    children: /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsxs(StyledTypography, {
                                color: Core.text.subdued,
                                children: [
                                    displayableName,
                                    " "
                                ]
                            }),
                            /*#__PURE__*/ _jsx(Typography, {
                                children: selectedVariationValue
                            })
                        ]
                    })
                }),
                showSizeChartLinkForPresetPrepack ? /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsx(Spacer, {
                            height: 8
                        }),
                        /*#__PURE__*/ _jsx(Row, {
                            children: /*#__PURE__*/ _jsx(SizeChartLink, {
                                brand: brand,
                                product: viewState.product
                            })
                        })
                    ]
                }) : null,
                /*#__PURE__*/ _jsx(Spacer, {
                    size: "3x"
                })
            ]
        });
    }
    if (displayType !== IProductVariationMap.IEntry.VariationDisplayType.DROPDOWN) {
        return /*#__PURE__*/ _jsx(SpecialOptionPicker, {
            variationName: variationName,
            variantOptionSelectionViewState: viewState,
            variationDisplayType: displayType,
            alt: alt,
            brand: brand
        });
    }
    return /*#__PURE__*/ _jsxs(Column, {
        style: {
            width: "100%"
        },
        children: [
            /*#__PURE__*/ _jsxs(Row, {
                justify: "space-between",
                align: "flex-end",
                children: [
                    /*#__PURE__*/ _jsx(Typography, {
                        as: "label",
                        htmlFor: dropDownRefId,
                        children: displayableName
                    }),
                    showSizeChartLink ? /*#__PURE__*/ _jsxs(Column, {
                        children: [
                            /*#__PURE__*/ _jsx(SizeChartLink, {
                                brand: brand,
                                product: viewState.product
                            }),
                            /*#__PURE__*/ _jsx(Spacer, {
                                height: 6
                            })
                        ]
                    }) : null
                ]
            }),
            /*#__PURE__*/ _jsx(Spacer, {
                size: 4
            }),
            /*#__PURE__*/ _jsx("div", {
                style: {
                    width: "100%"
                },
                tabIndex: 1,
                role: "button",
                children: /*#__PURE__*/ _jsx(StyledDropdown, {
                    id: dropDownRefId,
                    options: dropDownOptions,
                    onSelect: function(option) {
                        trackProductInfoProductDetailsVariantSelectionClick(displayType.toString());
                        viewState.handleVariationSelect(variationName, option.value);
                    },
                    placeholder: selectedVariationValue !== null && selectedVariationValue !== void 0 ? selectedVariationValue : strictLocalize({
                        id: "OGp4N5",
                        defaultMessage: [
                            {
                                type: 0,
                                value: "Unavailable"
                            }
                        ]
                    }),
                    disabled: dropDownOptions.length === 1 && viewState.hasValidOptionBasedOnSelectedVariations,
                    disableInput: true
                })
            }),
            /*#__PURE__*/ _jsx(Spacer, {
                size: "3x"
            })
        ]
    });
};
var getDisplayType = function(variationName, viewState, variationOptions, variationDisplayType) {
    if (variationDisplayType) {
        return variationDisplayType;
    }
    if ((variationOptions === null || variationOptions === void 0 ? void 0 : variationOptions.length) === 1) {
        return IProductVariationMap.IEntry.VariationDisplayType.SINGLE;
    }
    if (viewState.allOptionsHaveSwatches(variationName)) {
        return IProductVariationMap.IEntry.VariationDisplayType.SWATCH;
    } else if (allOptionsAreShort(variationOptions)) {
        return IProductVariationMap.IEntry.VariationDisplayType.SHORT;
    }
    return IProductVariationMap.IEntry.VariationDisplayType.DROPDOWN;
};
var allOptionsAreShort = function(variationOptions) {
    var _variationOptions_every;
    return (_variationOptions_every = variationOptions === null || variationOptions === void 0 ? void 0 : variationOptions.every(function(variation) {
        return variation.value && variation.value.length <= SHORT_OPTION_LENGTH;
    })) !== null && _variationOptions_every !== void 0 ? _variationOptions_every : false;
};
export var SingleVariationSelector = observer(SingleVariationSelectorBase);
var StyledDropdown = styled(Dropdown).withConfig({
    componentId: "sc-9a16d789-0"
})([
    "width:100%;input{text-align:center;}"
]);
var StyledTypography = styled(Typography).withConfig({
    componentId: "sc-9a16d789-1"
})([
    "white-space:pre;"
]);
